import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { aetnaPlanSchema } from "~/schemas/SignUp/InsuranceProvider";
import { RadioCardGroup } from "~/shared/components/ui/RadioCardGroup";
import { useAutoAdvanceOnSelect } from "~/shared/hooks/useAutoAdvanceOnSelect";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import { AETNA_PLAN_COVERAGES } from "~/shared/utils/constants";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { shouldFirePixelEvents } from "../../../../utils/pixelUtils";
import { getUtmParams } from "../../../../utils/utmCapture";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

export const AetnaPlanForm = ({
  onPrev,
  onNext,
  stepId = "primary_insurance",
  isSecondaryProvider = false,
}: InsuranceProviderFormProps & { stepId?: string }) => {
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const planTypeProvider = isSecondaryProvider
    ? secondaryInsurance?.planTypeProvider
    : primaryInsurance.planTypeProvider;

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.aetnaPlan
    : primaryInsurance.aetnaPlan;

  const [selectedValue, setSelectedValue] = useState<string>(
    defaultValue ?? "",
  );
  const autoAdvanceOnSelect = useAutoAdvanceOnSelect(Boolean(defaultValue));

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "aetna_plan",
  });

  const aetnaOptions = AETNA_PLAN_COVERAGES.map((option) => ({
    value: option,
    label: option,
  }));

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof aetnaPlanSchema>>({
    mode: "onBlur",
    resolver: zodResolver(aetnaPlanSchema),
    defaultValues: {
      aetnaPlan: defaultValue ?? "",
    },
  });

  useEffect(() => {
    setValue("aetnaPlan", defaultValue ?? "");
    setSelectedValue(defaultValue ?? "");
  }, [defaultValue, setValue]);

  const handleOptionChange = (value: string) => {
    setSelectedValue(value);
    setValue("aetnaPlan", value);
    // On the frontier (first encounter), a selection advances immediately;
    // on a revisited step the user changes freely and clicks Next.
    if (autoAdvanceOnSelect) {
      void handleSubmit(onSubmit, onError)();
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof aetnaPlanSchema>> = (data) => {
    setPatientInsuranceField("aetnaPlan", data.aetnaPlan, isSecondaryProvider);
    setPatientInsuranceField(
      "planCoverage",
      data.aetnaPlan,
      isSecondaryProvider,
    );
    trackComplete({ aetna_plan: data.aetnaPlan });

    if (
      !isSecondaryProvider &&
      data.aetnaPlan === "PPO" &&
      planTypeProvider === "employer" &&
      shouldFirePixelEvents()
    ) {
      const utm = getUtmParams();
      const fbq = (window as any).fbq;
      if (fbq) {
        fbq("track", "Lead", {
          content_name: "Plan Selected",
          ...(utm?.utm_source && { utm_source: utm.utm_source }),
          ...(utm?.utm_medium && { utm_medium: utm.utm_medium }),
          ...(utm?.utm_campaign && { utm_campaign: utm.utm_campaign }),
          ...(utm?.utm_term && { utm_term: utm.utm_term }),
          ...(utm?.utm_content && { utm_content: utm.utm_content }),
          ...(utm?.ad_set_id && { adset_id: utm.ad_set_id }),
          ...(utm?.ad_set_name && { adset_name: utm.ad_set_name }),
          ...(utm?.ad_id && { ad_id: utm.ad_id }),
          ...(utm?.ad_name && { ad_name: utm.ad_name }),
        });
      }
    }

    // Set network status based on plan selection
    const isOutOfNetwork = data.aetnaPlan === "HMO";
    setPatientInsuranceField(
      "isOutOfNetwork",
      isOutOfNetwork,
      isSecondaryProvider,
    );

    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField("aetnaPlan", undefined, isSecondaryProvider);
    setPatientInsuranceField("planCoverage", undefined, isSecondaryProvider);
    setPatientInsuranceField("isOutOfNetwork", undefined, isSecondaryProvider);
    trackBack();
    onPrev();
  };

  const canProceed = selectedValue !== "";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="w-full max-w-[570px] rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-16 self-center sm:size-20" />
        <h1 className="font-display text-xl sm:text-2xl">
          What type of Aetna plan do you have?
        </h1>

        <RadioCardGroup
          options={aetnaOptions}
          value={selectedValue}
          onChange={handleOptionChange}
        />

        {errors.aetnaPlan && (
          <ErrorMessage message={errors.aetnaPlan.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons
        onPrev={handlePrev}
        isNextDisabled={!canProceed}
        helperText="Select a plan type to continue"
      />
    </form>
  );
};
